import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, MapPin, Clock, Building2, CheckCircle2, Circle, Briefcase, Loader2, XCircle, DollarSign, Gift, ListOrdered, ExternalLink } from "lucide-react";

import { useJobPostDetail } from "@/hooks/useJobPosts";
import { useStudentSkills } from "@/hooks/useStudentSkills";
import { calculateMatchScore, getSkillById } from "@/lib/skills";
import { MatchBadge } from "@/components/MatchBadge";
import { getCompanyName, getCompanyLogoUrl, isExternalJob, getExternalApplyUrl, getSourceName, getSourceLabel, isCrawlerJob, withUtm } from "@/lib/jobUtils";

export default function AlunoVagaDetalhe() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { data: vaga, isLoading } = useJobPostDetail(id);
  const { skills: mySkills } = useStudentSkills();

  if (isLoading) {
    return <div className="page-container flex items-center justify-center min-h-[50vh]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }
  if (!vaga) {
    return <div className="page-container text-center py-12"><p className="text-muted-foreground">Vaga não encontrada</p><Button variant="outline" className="mt-4" onClick={() => navigate("/aluno/vagas")}>Voltar</Button></div>;
  }

  const v = vaga as any;
  const empresa = getCompanyName(vaga);
  const logoUrl = getCompanyLogoUrl(vaga);
  const isExternal = isExternalJob(vaga);
  const externalUrl = getExternalApplyUrl(vaga);
  const sourceName = getSourceName(vaga);
  const responsabilidades = Array.isArray(vaga.responsibilities) ? (vaga.responsibilities as string[]) : [];
  const requisitosObrigatorios = Array.isArray(vaga.requirements_must) ? (vaga.requirements_must as string[]) : [];
  const requisitosDesejaveis = Array.isArray(v.requirements_nice) ? (v.requirements_nice as string[]) : [];
  const requiredSkills = Array.isArray(vaga.required_skills) ? (vaga.required_skills as string[]) : [];
  const niceSkills = Array.isArray(vaga.nice_skills) ? (vaga.nice_skills as string[]) : [];
  const benefits = Array.isArray(v.benefits) ? (v.benefits as string[]) : [];
  const processSteps = Array.isArray(v.process_steps) ? (v.process_steps as string[]) : [];
  const stipendMin = v.stipend_min as number | null;
  const stipendMax = v.stipend_max as number | null;

  const matchResult = calculateMatchScore(mySkills, requiredSkills, niceSkills);
  const hasMatchData = (requiredSkills.length > 0 || niceSkills.length > 0) && mySkills.length > 0;

  const formatCurrency = (val: number) => `R$ ${val.toLocaleString("pt-BR")}`;

  return (
    <div className="page-container">
      <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate("/aluno/vagas")}><ArrowLeft className="w-5 h-5" /></Button>
        <span className="text-sm text-muted-foreground">Detalhe da vaga</span>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        className="bg-card rounded-2xl p-5 shadow-card border border-border mb-6">
        <div className="flex items-start gap-3 mb-4">
          {logoUrl ? (
            <img src={logoUrl} alt="" className="w-14 h-14 rounded-xl object-contain border border-border" />
          ) : (
            <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
              <Building2 className="w-7 h-7 text-primary" />
            </div>
          )}
          <div className="flex-1">
            <Badge variant="secondary" className="mb-2">{vaga.employment_type}</Badge>
            <h1 className="font-heading text-xl font-bold text-foreground">{vaga.title}</h1>
            <p className="text-muted-foreground">{empresa}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-1 bg-secondary px-3 py-1 rounded-full">
            <MapPin className="w-4 h-4" />{vaga.work_model ?? "Presencial"} {vaga.location_city && `• ${vaga.location_city}, ${vaga.location_state}`}
          </span>
        </div>
        {vaga.deadline_date && (
          <div className="mt-4 p-3 bg-accent/10 rounded-xl border border-accent/20">
            <p className="text-xs text-muted-foreground flex items-center gap-2"><Clock className="w-4 h-4" />Prazo: {vaga.deadline_date}</p>
          </div>
        )}
      </motion.div>

      {/* Stipend */}
      {(stipendMin || stipendMax) && (
        <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="mb-6">
          <div className="bg-card rounded-2xl p-4 border border-border flex items-center gap-3">
            <DollarSign className="w-5 h-5 text-success" />
            <div>
              <p className="text-sm font-medium text-foreground">Bolsa-auxílio</p>
              <p className="text-sm text-muted-foreground">
                {stipendMin && stipendMax ? `${formatCurrency(stipendMin)} – ${formatCurrency(stipendMax)}` : stipendMin ? `A partir de ${formatCurrency(stipendMin)}` : `Até ${formatCurrency(stipendMax!)}`}
              </p>
            </div>
          </div>
        </motion.section>
      )}

      {vaga.description && (
        <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mb-6">
          <h2 className="font-heading text-base font-semibold text-foreground mb-3">Descrição</h2>
          <p className="text-sm text-muted-foreground leading-relaxed">{vaga.description}</p>
        </motion.section>
      )}

      {responsabilidades.length > 0 && (
        <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="mb-6">
          <h2 className="font-heading text-base font-semibold text-foreground mb-3">Responsabilidades</h2>
          <ul className="space-y-2">
            {responsabilidades.map((resp, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground"><Circle className="w-2 h-2 mt-2 shrink-0 fill-current" />{resp}</li>
            ))}
          </ul>
        </motion.section>
      )}

      {requisitosObrigatorios.length > 0 && (
        <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mb-6">
          <h2 className="font-heading text-base font-semibold text-foreground mb-3">Requisitos Obrigatórios</h2>
          <ul className="space-y-2">
            {requisitosObrigatorios.map((req, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-foreground"><CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-success" />{req}</li>
            ))}
          </ul>
        </motion.section>
      )}

      {requisitosDesejaveis.length > 0 && (
        <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.32 }} className="mb-6">
          <h2 className="font-heading text-base font-semibold text-foreground mb-3">Requisitos Desejáveis</h2>
          <ul className="space-y-2">
            {requisitosDesejaveis.map((req, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground"><Circle className="w-2 h-2 mt-2 shrink-0 fill-current" />{req}</li>
            ))}
          </ul>
        </motion.section>
      )}

      {(requiredSkills.length > 0 || niceSkills.length > 0) && (
        <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }} className="mb-6">
          <h2 className="font-heading text-base font-semibold text-foreground mb-3">Habilidades</h2>
          <div className="flex flex-wrap gap-2">
            {requiredSkills.map((s, i) => <Badge key={i} variant="default" className="text-xs">{getSkillById(s)?.label ?? s}</Badge>)}
            {niceSkills.map((s, i) => <Badge key={i} variant="outline" className="text-xs">{getSkillById(s)?.label ?? s}</Badge>)}
          </div>
        </motion.section>
      )}

      {/* Benefits */}
      {benefits.length > 0 && (
        <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.37 }} className="mb-6">
          <h2 className="font-heading text-base font-semibold text-foreground mb-3 flex items-center gap-2"><Gift className="w-4 h-4" /> Benefícios</h2>
          <div className="flex flex-wrap gap-2">
            {benefits.map((b, i) => <Badge key={i} variant="secondary" className="text-xs">{b}</Badge>)}
          </div>
        </motion.section>
      )}

      {/* Process Steps */}
      {processSteps.length > 0 && (
        <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.39 }} className="mb-8">
          <h2 className="font-heading text-base font-semibold text-foreground mb-3 flex items-center gap-2"><ListOrdered className="w-4 h-4" /> Etapas do Processo</h2>
          <ol className="space-y-2">
            {processSteps.map((s, i) => (
              <li key={i} className="flex items-center gap-3 text-sm">
                <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                <span className="text-foreground">{s}</span>
              </li>
            ))}
          </ol>
        </motion.section>
      )}

      {/* Match Comparison Block */}
      {hasMatchData && (
        <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="mb-8">
          <div className="bg-card rounded-2xl p-5 border border-border shadow-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-base font-semibold text-foreground">Seu Match</h2>
              <MatchBadge percentage={matchResult.percentage} size="lg" />
            </div>
            {(matchResult.matchedRequired.length > 0 || matchResult.matchedNice.length > 0) && (
              <div className="mb-3">
                <p className="text-xs font-medium text-muted-foreground mb-2">✅ Habilidades em comum</p>
                <div className="flex flex-wrap gap-1.5">
                  {matchResult.matchedRequired.map((s, i) => (<Badge key={`mr-${i}`} variant="default" className="text-xs gap-1"><CheckCircle2 className="w-3 h-3" />{getSkillById(s)?.label ?? s}</Badge>))}
                  {matchResult.matchedNice.map((s, i) => (<Badge key={`mn-${i}`} variant="secondary" className="text-xs gap-1"><CheckCircle2 className="w-3 h-3" />{getSkillById(s)?.label ?? s}</Badge>))}
                </div>
              </div>
            )}
            {(matchResult.missingRequired.length > 0 || matchResult.missingNice.length > 0) && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">📚 Habilidades para desenvolver</p>
                <div className="flex flex-wrap gap-1.5">
                  {matchResult.missingRequired.map((s, i) => (<Badge key={`xr-${i}`} variant="outline" className="text-xs gap-1 border-destructive/30 text-destructive"><XCircle className="w-3 h-3" />{getSkillById(s)?.label ?? s}</Badge>))}
                  {matchResult.missingNice.map((s, i) => (<Badge key={`xn-${i}`} variant="outline" className="text-xs gap-1"><XCircle className="w-3 h-3" />{getSkillById(s)?.label ?? s}</Badge>))}
                </div>
              </div>
            )}
          </div>
        </motion.section>
      )}

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
        className="sticky bottom-20 bg-background pt-4">
        {isExternal ? (
          <Button variant="hero" size="xl" className="w-full gap-2" onClick={() => window.open(withUtm(externalUrl!), "_blank")}>
            <ExternalLink className="w-5 h-5" />
            Ver vaga no {getSourceLabel(sourceName)}
          </Button>
        ) : (
          <Button variant="hero" size="xl" className="w-full" onClick={() => navigate(`/aluno/vagas/${id}/aplicar`)}>
            Aplicar para esta vaga
          </Button>
        )}
        {isExternal && (
          <p className="text-xs text-muted-foreground text-center mt-2">Candidatura no site original</p>
        )}
      </motion.div>
    </div>
  );
}
